import { ThemeEvents } from '@theme/events';

/**
 * Autóillatosító akció popup (2+1, 3+2, 4+3 ajándék).
 *
 * Kosárba rakás után röviden megmutatja, hány darab hiányzik a következő ajándékszinthez. Direkt visszafogott:
 *  - nincs sötétítő háttér, az oldal végig használható marad
 *  - nincs bezáró gomb: egy narancs csík visszafelé fogy, majd a kártya eltűnik (Esc-kel is zárható)
 *  - a csík egérrel/fókusszal megállítható
 *  - csak akkor jelenik meg, ha a kosárban az akciós termékek száma nőtt (hozzáadás volt)
 *  - a legmagasabb szint elérése után nem jelenik meg
 *
 * Elhelyezés: számítógépen kis kártya jobb felül (a fejléc alatt); telefonon a menühöz hasonló, alulról felcsúszó,
 * teljes szélességű lap szürke háttérrel. Ha a süti-sáv látszik, telefonon is a kis felső kártya jön, mert a sáv
 * mindig a többi elem fölött van, és két egymásra rakott panel zavaró volna.
 */

const TEEINBLUE_AFTER_CART_ADDED = 'teeinblue-event-after-cart-added';
// A teeinblue ezt a saját eseményét minden /cart/add|change|update|clear hívás után elsüti. Kiegészítő jelzés arra az
// esetre, ha az űrlap más úton rakná a kosárba; a darabszám-növekedés vizsgálata miatt a téves jelzés nem árt.
const TEEINBLUE_CART_MODIFIED = 'shopify-event-cart-modified';
const SCHEDULE_DELAY_MS = 700;
const AUTO_HIDE_MS = 10000;
const EXIT_ANIMATION_MS = 400;
const CONSENT_BANNER_ID = 'shopify-pc__banner';
const DESKTOP_QUERY = '(min-width: 750px)';

/** @typedef {{ qty: number, free: number }} Tier */

/** Számnév ragja a tárgyesethez: 3-at, 5-öt, 7-et (magyar ragozás, a szintekben szereplő számokra). */
const NUMBER_SUFFIX = { 1: 'et', 2: 't', 3: 'at', 4: 'et', 5: 'öt', 6: 'ot', 7: 'et', 8: 'at', 9: 'et', 10: 'et' };

/** @param {number} n */
const withSuffix = (n) => `${n}-${NUMBER_SUFFIX[/** @type {keyof typeof NUMBER_SUFFIX} */ (n)] ?? 't'}`;

/** @param {string} text */
const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

class BundlePopup extends HTMLElement {
  /** @type {Tier[]} */
  #tiers = [];
  #match = '';
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #scheduleTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hideTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #exitTimer;
  #remainingMs = AUTO_HIDE_MS;
  #timerStartedAt = 0;
  /** Az akciós termékek darabszáma az utolsó ismert kosárban. */
  #lastCount = 0;
  /** @type {Promise<void>} */
  #baseline = Promise.resolve();

  connectedCallback() {
    try {
      const config = JSON.parse(this.dataset.config ?? '{}');
      this.#tiers = (config.tiers ?? []).slice().sort((/** @type {Tier} */ a, /** @type {Tier} */ b) => a.qty - b.qty);
      this.#match = String(config.match ?? '').toLowerCase();
    } catch (_) {
      return;
    }
    if (!this.#tiers.length || !this.#match) return;

    this.style.setProperty('--kk-bp-duration', `${AUTO_HIDE_MS}ms`);

    document.addEventListener(TEEINBLUE_AFTER_CART_ADDED, this.#schedule);
    document.addEventListener(TEEINBLUE_CART_MODIFIED, this.#schedule);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('pageshow', this.#onPageShow);

    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    this.querySelector('[data-bp-more]')?.addEventListener('click', this.#onMore);
    // Telefonon a szürke háttérre kattintva (a menühöz hasonlóan) bezárul a panel.
    this.querySelector('[data-bp-backdrop]')?.addEventListener('click', () => this.#dismiss());
    this.#setupSwipeToDismiss();

    // Kiinduló darabszám, hogy a későbbi változásból tudjuk: hozzáadás történt-e.
    this.#baseline = this.#syncBaseline();
  }

  disconnectedCallback() {
    document.removeEventListener(TEEINBLUE_AFTER_CART_ADDED, this.#schedule);
    document.removeEventListener(TEEINBLUE_CART_MODIFIED, this.#schedule);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('pageshow', this.#onPageShow);
    clearTimeout(this.#scheduleTimer);
    clearTimeout(this.#hideTimer);
    clearTimeout(this.#exitTimer);
  }

  /**
   * Telefonon a lapot a fogantyúnál (vagy bárhol rajta, a gombokat kivéve) lefelé húzva be lehet zárni, ahogy a
   * menüt is. Elég nagy húzásra vagy gyors lendületre zárul, különben visszaugrik a helyére. A fogantyúra
   * koppintva is bezárul.
   */
  #setupSwipeToDismiss() {
    const card = /** @type {HTMLElement | null} */ (this.querySelector('.kk-bp__card'));
    if (!card) return;

    const DISMISS_DISTANCE = 70;
    const DISMISS_VELOCITY = 0.5;
    const TAP_TOLERANCE = 6;

    let dragging = false;
    let startedOnGrabber = false;
    let startY = 0;
    let startTime = 0;
    let offset = 0;
    let pointerId = -1;

    card.addEventListener('pointerdown', (event) => {
      if (this.dataset.pos !== 'bottom' || !this.classList.contains('is-visible')) return;
      if (/** @type {Element} */ (event.target).closest('a, button')) return;

      dragging = true;
      startedOnGrabber = Boolean(/** @type {Element} */ (event.target).closest('.kk-bp__grabber'));
      startY = event.clientY;
      startTime = Date.now();
      offset = 0;
      pointerId = event.pointerId;

      try {
        card.setPointerCapture(pointerId);
      } catch (_) {
        // no-op
      }
      card.style.transition = 'none';
      this.#pause();
    });

    card.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      offset = Math.max(0, event.clientY - startY);
      card.style.transform = `translateY(${offset}px)`;
    });

    const finish = () => {
      if (!dragging) return;
      dragging = false;

      try {
        card.releasePointerCapture(pointerId);
      } catch (_) {
        // no-op
      }

      const elapsed = Math.max(1, Date.now() - startTime);
      const shouldDismiss =
        offset > DISMISS_DISTANCE || offset / elapsed > DISMISS_VELOCITY || (startedOnGrabber && offset < TAP_TOLERANCE);

      // Az inline stílusokat töröljük: a lap a húzás helyéről animálódik tovább (le, vagy vissza a helyére).
      card.style.transition = '';
      card.style.transform = '';

      if (shouldDismiss) {
        this.#dismiss();
      } else {
        this.#resume();
      }
    };

    card.addEventListener('pointerup', finish);
    card.addEventListener('pointercancel', finish);
  }

  /** Vissza/előre navigálásnál a gyorsítótárból visszatöltött oldal kosárállapota elavulhat. */
  #onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) this.#baseline = this.#syncBaseline();
  };

  async #syncBaseline() {
    try {
      this.#lastCount = this.#eligibleCount(await this.#fetchCart());
    } catch (_) {
      this.#lastCount = 0;
    }
  }

  async #fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('cart.js');
    return response.json();
  }

  /**
   * A téma saját űrlapja (nem teeinblue) is kosárba rakhat: csak ezt a forrást figyeljük, hogy a
   * teeinblue-hoz írt cart-sync által küldött esemény ne váltson ki másodszor is.
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const data = /** @type {CustomEvent} */ (event).detail?.data;
    if (data?.source === 'product-form-component' && !data.didError) this.#schedule();
  };

  /** Egymás utáni események (pl. több hozzáadás) esetén csak az utolsó után dolgozunk. */
  #schedule = () => {
    clearTimeout(this.#scheduleTimer);
    this.#scheduleTimer = setTimeout(() => this.#check(), SCHEDULE_DELAY_MS);
  };

  async #check() {
    try {
      await this.#baseline;
      const count = this.#eligibleCount(await this.#fetchCart());
      const previous = this.#lastCount;
      this.#lastCount = count;

      // Csak hozzáadásnál szólunk (törlésnél, mennyiségcsökkentésnél nem).
      if (count <= previous || count < 1) return;

      // Legmagasabb szint elérve: nincs mit ajánlani, nem zavarunk tovább.
      const top = this.#tiers[this.#tiers.length - 1];
      if (count >= top.qty) return;

      this.#show(count);
    } catch (_) {
      // no-op
    }
  }

  /**
   * Az akcióba tartozó termékek darabszáma a kosárban.
   * @param {{ items?: Array<{ handle?: string, url?: string, quantity: number }> }} cart
   */
  #eligibleCount(cart) {
    return (cart.items ?? []).reduce((sum, item) => {
      // Kötőjelek nélkül hasonlítunk: van termék, amelynek a kezelője 'auto-illatosito'.
      const key = `${item.handle ?? ''} ${item.url ?? ''}`.toLowerCase().replace(/-/g, '');
      return key.includes(this.#match) ? sum + item.quantity : sum;
    }, 0);
  }

  /** @param {number} count */
  #show(count) {
    const reached = [...this.#tiers].reverse().find((tier) => tier.qty <= count);
    const next = this.#tiers.find((tier) => tier.qty > count);
    if (!next) return;

    const missing = next.qty - count;
    const pay = next.qty - next.free;
    const justReached = Boolean(reached) && count === reached?.qty;

    // Rövid, egy pillantásra érthető üzenet: a cím megmondja, hogy ingyen kapnak autóillatosítót, az alcím hogy
    // hogyan jön ki a számolás.
    const offer = `vigyél ${withSuffix(next.qty)}, fizess csak ${withSuffix(pay)}!`;
    let title;
    let text;
    if (justReached && reached) {
      title = `Megvan: ${reached.free} autóillatosítót ingyen kapsz!`;
      text = `Még ${missing} db-bal már ${next.free} ingyen jár: ${offer}`;
    } else {
      title = `Még ${missing} db, és ${next.free} autóillatosítót ingyen kapsz`;
      text = capitalize(offer);
    }

    this.#setText('[data-bp-title]', title);
    this.#setText('[data-bp-text]', text);
    this.#setIcon(justReached);
    this.#renderDots(count, next.qty);

    clearTimeout(this.#exitTimer);
    this.#position();
    this.#restartTimerBar();

    const alreadyVisible = this.classList.contains('is-visible');
    this.hidden = false;
    if (!alreadyVisible) {
      // Az offsetWidth olvasása kikényszeríti a stílusszámítást, így az osztály hozzáadásakor a felugrás lefut.
      void this.offsetWidth;
      this.classList.add('is-visible');
    }

    this.#startHideTimer(AUTO_HIDE_MS);
  }

  /** Számítógépen jobb felül, telefonon alul; látszó süti-sávnál telefonon is felül. */
  #position() {
    const desktop = window.matchMedia(DESKTOP_QUERY).matches;
    const onTop = desktop || this.#isConsentBannerVisible();
    this.dataset.pos = onTop ? 'top' : 'bottom';

    if (onTop) {
      // A fejléc aktuális alsó széléhez igazítunk: lehet fix, görgetésre eltűnő, vagy a hirdetősáv alatt álló.
      const header = document.querySelector('header-component');
      const bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      this.style.setProperty('--kk-bp-top', `${Math.round(bottom) + 12}px`);
    }
  }

  #isConsentBannerVisible() {
    const banner = document.getElementById(CONSENT_BANNER_ID);
    if (!banner) return false;
    const rect = banner.getBoundingClientRect();
    return rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0;
  }

  /** A fogyó csík animációját elölről indítjuk (pl. ha a kártya közben új darabszámra frissül). */
  #restartTimerBar() {
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-bp-timer]'));
    if (!bar) return;
    bar.style.animation = 'none';
    void bar.offsetWidth;
    bar.style.animation = '';
    this.removeAttribute('data-paused');
  }

  /**
   * Alap állapotban az ajándékdoboz ikon látszik, egy szint éppen elért állapotában a pipás változat.
   * @param {boolean} reached
   */
  #setIcon(reached) {
    const gift = /** @type {HTMLElement | null} */ (this.querySelector('[data-bp-icon="gift"]'));
    const check = /** @type {HTMLElement | null} */ (this.querySelector('[data-bp-icon="check"]'));
    if (gift) gift.hidden = reached;
    if (check) check.hidden = !reached;
  }

  /**
   * Egy sor pötty a következő ajándékszintig: a teli pöttyök a már kosárban lévő darabok, az üresek a hiányzók,
   * az utolsó (cél) pötty kiemelt.
   * @param {number} count
   * @param {number} goal
   */
  #renderDots(count, goal) {
    const container = this.querySelector('[data-bp-dots]');
    if (!container) return;

    const dots = Array.from({ length: goal }, (_, index) => {
      const dot = document.createElement('span');
      dot.className = 'kk-bp__dot';
      if (index < count) dot.classList.add('is-filled');
      if (index === goal - 1) dot.classList.add('is-goal');
      return dot;
    });
    container.replaceChildren(...dots);
  }

  /**
   * @param {string} selector
   * @param {string} value
   */
  #setText(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }

  /** @param {number} ms */
  #startHideTimer(ms) {
    clearTimeout(this.#hideTimer);
    this.#remainingMs = ms;
    this.#timerStartedAt = Date.now();
    this.#hideTimer = setTimeout(() => this.#dismiss(), ms);
  }

  /** Egérrel vagy fókusszal megállítjuk a visszaszámlálást (a csík animációja is megáll). */
  #pause = () => {
    if (this.hidden || !this.classList.contains('is-visible') || this.hasAttribute('data-paused')) return;
    clearTimeout(this.#hideTimer);
    this.#remainingMs = Math.max(0, this.#remainingMs - (Date.now() - this.#timerStartedAt));
    this.setAttribute('data-paused', '');
  };

  #resume = () => {
    if (!this.hasAttribute('data-paused')) return;
    this.removeAttribute('data-paused');
    this.#startHideTimer(Math.max(this.#remainingMs, 400));
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && !this.hidden) this.#dismiss();
  };

  /**
   * "Még egyet tervezek": alaphelyzetbe állítjuk a tervezőt (a feltöltött képek törlődnek), eltüntetjük a
   * kártyát, és csak addig görgetünk, hogy az első fotófeltöltő mező látszódjon.
   */
  #onMore = () => {
    this.#resetDesigner();
    this.#dismiss();
    // Kis várakozás, hogy a teeinblue újrarajzolása után mérjünk.
    setTimeout(() => this.#scrollToUploadField(), 150);
  };

  /**
   * A teeinblue hivatalos API-jával töröljük a mentett testreszabást, és újraindítjuk az űrlap adatait.
   * Az illat/kiszerelés választás a Shopify variáns része, azt nem érinti.
   */
  #resetDesigner() {
    const win = /** @type {any} */ (window);
    const productId = win.teeinblueCampaign?.productId;
    if (typeof win.teeinblue?.resetCustomization !== 'function' || !productId) return;

    try {
      win.teeinblue.resetCustomization(productId);
    } catch (_) {
      // no-op
    }
  }

  /**
   * Csak a legszükségesebb mértékben görget: ha az első fotófeltöltő mező címkéje már látszik, nem mozdul az
   * oldal, egyébként épp addig, hogy a (fix fejléc alatt) látszódjon.
   */
  #scrollToUploadField() {
    const field = document.querySelector('.tee-field--photo');
    const target = field?.querySelector('.tee-field__header') ?? field;
    if (!target) return;

    const header = document.querySelector('header-component');
    const margin = 16;
    const minTop = (header ? header.offsetHeight : 0) + margin;
    const rect = target.getBoundingClientRect();

    if (rect.top >= minTop && rect.bottom <= window.innerHeight - margin) return;

    window.scrollBy({ top: rect.top - minTop, behavior: 'smooth' });
  }

  #dismiss() {
    clearTimeout(this.#hideTimer);
    this.removeAttribute('data-paused');
    this.classList.remove('is-visible');

    // Az eltűnés után tényleg elrejtjük, hogy a segédtechnológiák se lássák.
    clearTimeout(this.#exitTimer);
    this.#exitTimer = setTimeout(() => {
      if (!this.classList.contains('is-visible')) this.hidden = true;
    }, EXIT_ANIMATION_MS);
  }
}

if (!customElements.get('bundle-popup')) {
  customElements.define('bundle-popup', BundlePopup);
}
